/* @jsxImportSource @emotion/react */
import React from 'react'

import { Avatar } from '../../Avatar/Avatar'
import { SanityTextRenderer } from '../../SanityComponents/SanityTextRenderer'
import { fontSize } from './constants'
import type { ArticleAuthor } from './types'

const PICTURE_SIZE = 64

const rowCss = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 20,
  padding: '24px 0',
  '&:first-of-type': { paddingTop: 0 },
  '&:last-of-type': { paddingBottom: 0 },
  '@media screen and (max-width: 20rem)': { flexDirection: 'column', gap: 12 },
} as const

// Avatar sets its size inline (max 50px), so only an inline style beats it.
const pictureStyle = {
  width: PICTURE_SIZE,
  height: PICTURE_SIZE,
  borderRadius: 16,
}

export function ArticleAuthorBios(props: {
  authors: ArticleAuthor[]
  label?: string
}) {
  const authorsWithBio = props.authors.filter(
    (author) => author.bio && author.bio.length > 0
  )
  if (authorsWithBio.length === 0) {
    return null
  }

  return (
    <section
      className="padding-top-32"
      css={{ marginTop: 56 }}
      data-test-id="article-author-bios"
    >
      {props.label && (
        // Rich-text classes so the label matches the body's h2s.
        <div className="simple-rich-text simple-rich-text-sanity large margin-bottom-12">
          <h2>{props.label}</h2>
        </div>
      )}
      {authorsWithBio.map((author, index) => (
        <div key={`${author.name}-${index}`} css={rowCss}>
          <Avatar
            name={author.name}
            photoUrl={author.imageUrl}
            size="huge"
            style={pictureStyle}
            aria-hidden="true"
          />
          <div className="min-width-zero">
            <div
              className="simple-rich-text simple-rich-text-sanity large shade-10"
              css={{ fontSize: fontSize.body }}
            >
              {author.bio && (
                <SanityTextRenderer variant="website" content={author.bio} />
              )}
            </div>
            <div
              className="shade-05 font-weight-semibold margin-top-16"
              css={{ fontSize: fontSize.body }}
            >
              {author.name}
            </div>
          </div>
        </div>
      ))}
    </section>
  )
}
